import { useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Bundle Picker · who-am-I → here's-your-bundle
 * The Founder's persona picker. One click. The right bundle.
 * Legal is free forever and comes with every bundle · Founder ruling 2026-08-13.
 */
const PERSONAS = [
  {
    slug: "artists",
    title: "Artists & Creators",
    subtitle: "one voice, many canvases",
    bundle: ["Sage", "Quill", "Loom", "Legal (free)"],
    price_year: 300,
    reason: "The writing chair, the drafting chair, and the visual weaving chair — the three tools every artist needs to shape a piece, share it, and keep the drafts. Legal on the house.",
  },
  {
    slug: "entrepreneurs",
    title: "Entrepreneurs & Founders",
    subtitle: "build it, launch it, keep it running",
    bundle: ["Baker", "Postmaster", "AdSmith", "Sage", "Legal (free)"],
    price_year: 800,
    reason: "The builder, the social broadcaster, the campaign engine, and the writing chair — the four hands a solo founder needs to launch a real thing this month. Legal on the house.",
  },
  {
    slug: "tech",
    title: "Tech Teams",
    subtitle: "a small team's whole tool belt",
    bundle: ["Baker", "Sage", "Quill", "Loom", "Herald", "Legal (free)"],
    price_year: 1000,
    reason: "The builder, the writing chair, the drafting chair, the visual chair, and the admin sibling — everything a five-to-fifteen-person tech team needs and nothing they don't. Legal on the house.",
  },
  {
    slug: "basic-tech",
    title: "Basic Tech (solo)",
    subtitle: "a solo dev's clean workbench",
    bundle: ["Baker", "Sage", "Legal (free)"],
    price_year: 400,
    reason: "The builder and the writing chair. Two hands. Everything a solo developer needs to ship a real product without a support team. Legal on the house.",
  },
  {
    slug: "educators",
    title: "Educators & Teachers",
    subtitle: "lesson plans, communications, and craft",
    bundle: ["Sage", "Quill", "Postmaster", "Legal (free)"],
    price_year: 500,
    reason: "The writing chair for lesson plans, the drafting chair for materials, the broadcaster for parent communications and classroom social presence. Legal on the house.",
  },
  {
    slug: "families-and-care",
    title: "Families & Care Workers",
    subtitle: "coordination, records, and calm",
    bundle: ["Sage", "Herald", "Legal (free)"],
    price_year: 300,
    reason: "The writing chair for records and messages, the admin sibling for coordination — a small quiet toolset for the person caring for someone else. Legal on the house.",
  },
  {
    slug: "marketing",
    title: "Marketing Teams",
    subtitle: "campaigns end to end",
    bundle: ["AdSmith", "Postmaster", "Sage", "Quill", "Legal (free)"],
    price_year: 800,
    reason: "The campaign engine, the social broadcaster, the writing chair, and the drafting chair — the four hands a marketing team runs on. Legal on the house.",
  },
  {
    slug: "musician",
    title: "Musicians & Producers",
    subtitle: "sample rooms, mastering, release, and the words around it",
    bundle: ["Choir · Music Studio", "Sage", "Loom", "Postmaster", "AdSmith", "Quill", "Legal (free)"],
    price_year: 1500,
    reason: "The music room for tracks, samples, and mastering; the writing chair for lyrics and press notes; the drafting chair for the album booklet; the broadcaster and campaign engine for release week; the visual chair for cover art. Legal on the house.",
    is_pro: true,
  },
  {
    slug: "video-editor",
    title: "Video Editors & Filmmakers",
    subtitle: "the whole cutting bay in one seat",
    bundle: ["VidSmith · motion video", "Loom · stills & thumbnails", "Sage", "Quill", "Postmaster", "AdSmith", "Legal (free)"],
    price_year: 1800,
    reason: "Motion video with a shelf of rails (Veo 3, Sora 2, Higgsfield, fal.ai — pick your rail per shot), stills for thumbnails and posters, the writing chair for scripts and titles, the drafting chair for pitches, and the broadcast and campaign hands for release. Render costs pay-as-you-go on top. Legal on the house.",
    is_pro: true,
    note_line: "Includes the whole studio; per-second render costs on VidSmith are separate (starts at $0.80/sec on fal.ai).",
  },
  {
    slug: "graphic-artist",
    title: "Graphic Artists & Designers",
    subtitle: "concept, iterate, ship — every canvas",
    bundle: ["Loom", "Sage", "Quill", "Postmaster", "AdSmith", "Herald", "Legal (free)"],
    price_year: 1200,
    reason: "The visual weaving chair for illustration and layout, the writing chair for briefs and captions, the drafting chair for client decks, the broadcaster for the portfolio, the campaign engine for client work, and the admin sibling to keep invoices in one place. Legal on the house.",
    is_pro: true,
  },
  {
    slug: "public-service",
    title: "Public Service",
    subtitle: "Beacon and Medic are FREE forever",
    bundle: ["Beacon (free)", "Medic (free)", "Sage", "Herald", "Legal (free)"],
    price_year: 200,
    reason: "Beacon and Medic are free forever for public-service workers. Add Sage and Herald for the writing and coordination chairs — a working set at a working-person price. Legal on the house.",
    is_public_service: true,
  },
  {
    slug: "non-profit",
    title: "501(c)(3) Non-Profits",
    subtitle: "25% off the whole bundle",
    bundle: ["Baker", "Sage", "Quill", "Loom", "Postmaster", "AdSmith", "Herald", "Legal (free)"],
    price_year: 1200,
    price_year_discounted: 900,
    reason: "The full working set at a 25% discount for verified 501(c)(3) organizations. Legal on the house.",
    is_non_profit: true,
  },
  {
    slug: "buy-all",
    title: "Buy Everything",
    subtitle: "the whole studio, one price",
    bundle: ["All agents", "Choir · Music Studio", "VidSmith · motion video", "Legal (free)", "Beacon (free)", "Medic (free)"],
    price_year: 2000,
    reason: "The whole family, one flat yearly price. Every product, every agent, every future one baked into the family before the year is out. Music and motion-video studios included. Legal on the house.",
    is_full_studio: true,
  },
];

export default function BundlesPage() {
  const [picked, setPicked] = useState(null);

  return (
    <div className="min-h-screen bg-[#fbfaf6] text-[#1e1e1e]" data-testid="bundles-page">
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="border-b-2 border-[#b58c2a] pb-4">
          <span className="text-4xl font-serif text-[#2c3e2a]">Who are you?</span>
        </div>
        <p className="mt-4 text-lg font-serif text-[#4a5a48] max-w-3xl">
          Pick the card that sounds most like you. Your bundle appears. One click to the checkout — no walls,
          no salesperson, no forty-question form.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-12" data-testid="persona-grid">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PERSONAS.map((p) => (
            <button
              key={p.slug}
              type="button"
              onClick={() => setPicked(p)}
              className={
                "text-left bg-white border rounded p-5 transition " +
                (picked?.slug === p.slug
                  ? "border-[#2c3e2a] shadow-lg"
                  : "border-[#d9d3bd] hover:border-[#2c3e2a] hover:shadow")
              }
              data-testid={`persona-${p.slug}`}
            >
              <div className="text-lg font-serif font-bold text-[#2c3e2a]">{p.title}</div>
              <div className="text-sm font-serif italic text-[#7c8a75] mt-1">{p.subtitle}</div>
              <div className="mt-4 text-sm font-serif text-[#4a5a48]">
                <strong>{p.bundle.length} agents · </strong>
                {p.price_year_discounted ? (
                  <>
                    <s>${p.price_year}/yr</s>{" "}
                    <span className="text-[#2c3e2a] font-bold">${p.price_year_discounted}/yr</span>
                  </>
                ) : (
                  <span className="text-[#2c3e2a] font-bold">${p.price_year}/yr</span>
                )}
              </div>
            </button>
          ))}
        </div>
      </section>

      {picked && (
        <section className="max-w-4xl mx-auto px-6 pb-16" data-testid="picked-detail">
          <div className="bg-[#2c3e2a] text-[#fbfaf6] rounded p-8">
            <div className="text-sm font-serif italic text-[#c8b78a] uppercase tracking-wide">Your bundle</div>
            <h2 className="text-3xl font-serif mt-1">{picked.title}</h2>
            <p className="mt-3 text-base font-serif text-[#e8ddb8] leading-snug">{picked.reason}</p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="bundle-agents">
              {picked.bundle.map((agent) => (
                <span key={agent} className="border border-[#b58c2a] text-[#c8b78a] rounded px-3 py-1 text-sm font-serif">
                  {agent}
                </span>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-3 items-baseline">
              <div className="text-3xl font-serif" data-testid="bundle-price">
                {picked.price_year_discounted ? `$${picked.price_year_discounted}` : `$${picked.price_year}`}
                <span className="text-lg text-[#c8b78a] font-serif italic">/yr</span>
              </div>
              {picked.price_year_discounted && (
                <div className="text-sm font-serif italic text-[#c8b78a]">
                  (was ${picked.price_year} — 25% off for verified 501(c)(3))
                </div>
              )}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="/tiers"
                className="bg-[#b58c2a] text-[#2c3e2a] py-2 px-5 rounded font-serif font-bold hover:bg-[#c8a147] transition"
                data-testid="checkout-link"
              >
                Take me to checkout →
              </a>
              {picked.is_non_profit && (
                <a
                  href="/hardship"
                  className="border border-[#b58c2a] text-[#c8b78a] py-2 px-5 rounded font-serif hover:bg-[#b58c2a] hover:text-[#2c3e2a] transition"
                  data-testid="verify-nonprofit-link"
                >
                  Apply for the 501(c)(3) discount →
                </a>
              )}
              {picked.is_public_service && (
                <div className="border border-[#b58c2a] text-[#c8b78a] py-2 px-5 rounded font-serif italic">
                  Beacon + Medic on the house, forever.
                </div>
              )}
            </div>
            {picked.note_line && (
              <div className="mt-4 text-sm font-serif italic text-[#c8b78a]" data-testid="bundle-note-line">
                {picked.note_line}
              </div>
            )}
            {picked.is_pro && (
              <div className="mt-4 text-xs font-serif uppercase tracking-wide text-[#c8b78a]" data-testid="bundle-pro-badge">
                Pro bundle · built for full-time working artists
              </div>
            )}
          </div>
        </section>
      )}

      <footer className="max-w-6xl mx-auto px-6 py-8 border-t border-[#d9d3bd]">
        <p className="text-sm font-serif italic text-[#7c8a75]">
          None of the above quite fit? Pick "Buy Everything" for the whole studio, or just start with one agent
          from the store. No wrong door.
        </p>
      </footer>
    </div>
  );
}
